import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import "../styles/pageTransition.css";

const ENTER_DURATION = 620;
const EXIT_DURATION = 260;

const PageTurnTransition = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [phase, setPhase] = useState("idle");
  const timeoutRef = useRef(null);
  const destinationRef = useRef(null);
  const phaseRef = useRef("idle");

  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useLayoutEffect(() => {
    if (!destinationRef.current) {
      return;
    }

    destinationRef.current = null;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    setPhase("enter");

    timeoutRef.current = setTimeout(() => {
      setPhase("idle");
    }, ENTER_DURATION);
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    const handleClick = (event) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.altKey ||
        event.ctrlKey ||
        event.shiftKey
      ) {
        return;
      }

      const anchor = event.target.closest?.("a[href]");
      if (!anchor) {
        return;
      }

      if (anchor.closest(".button-language")) {
        return;
      }

      const href = anchor.getAttribute("href");
      if (
        !href ||
        href.startsWith("#") ||
        href.startsWith("mailto:") ||
        href.startsWith("tel:") ||
        anchor.hasAttribute("download") ||
        (anchor.target && anchor.target !== "_self")
      ) {
        return;
      }

      const nextUrl = new URL(anchor.href, window.location.href);
      if (nextUrl.origin !== window.location.origin) {
        return;
      }

      const currentPage = `${window.location.pathname}${window.location.search}`;
      const nextPage = `${nextUrl.pathname}${nextUrl.search}`;
      if (currentPage === nextPage) {
        return;
      }

      event.preventDefault();
      if (phaseRef.current !== "idle") {
        return;
      }

      const nextPath = `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`;
      destinationRef.current = nextPath;
      setPhase("leave");

      timeoutRef.current = setTimeout(() => {
        navigate(nextPath);
      }, EXIT_DURATION);
    };

    document.addEventListener("click", handleClick, true);
    return () => document.removeEventListener("click", handleClick, true);
  }, [navigate]);

  return (
    <div
      className={`page-transition page-transition--${phase}`}
    >
      <div className="page-transition__content">
        {children}
      </div>
    </div>
  );
};

export default PageTurnTransition;
